import React, { useState, useEffect } from 'react';
import { Palette, Type, Square, Save, RotateCcw, Check, Info, Image as ImageIcon, X, Code } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import SimpleTooltip from '../components/SimpleTooltip';
import MediaPickerModal from '../components/MediaPickerModal';
import FONT_FAMILIES from '../constants/fonts';
import { useBranding } from '../context/BrandingContext';

const ThemeSettings = () => {
  const { refreshBranding } = useBranding();
  const [theme, setTheme] = useState(null);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    logo_url: '', favicon_url: '', custom_head_html: '', custom_body_html: '',
    cookie_banner_enabled: false,
    cookie_banner_title: '',
    cookie_banner_message: '',
    cookie_banner_accept_text: '',
    cookie_banner_reject_text: '',
    cookie_banner_policy_url: '',
    physical_address: '',
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [addressDraft, setAddressDraft] = useState('');
  const [brandingPickerTarget, setBrandingPickerTarget] = useState(null); // 'logo' | 'favicon' | null
  // Local draft for the custom-code textareas — saved manually with a button
  // so we don't fire a PUT on every keystroke. (Logo/favicon save on pick.)
  const [customCodeDraft, setCustomCodeDraft] = useState({ custom_head_html: '', custom_body_html: '' });
  // Local draft for the cookie banner config — same pattern, single Save button.
  const [bannerDraft, setBannerDraft] = useState({
    cookie_banner_enabled: false,
    cookie_banner_title: '',
    cookie_banner_message: '',
    cookie_banner_accept_text: '',
    cookie_banner_reject_text: '',
    cookie_banner_policy_url: '',
  });

  useEffect(() => {
    fetchTheme();
    fetchPresets();
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await api.get('/tenant/branding');
      const next = {
        logo_url: response.data.logo_url || '',
        favicon_url: response.data.favicon_url || '',
        custom_head_html: response.data.custom_head_html || '',
        custom_body_html: response.data.custom_body_html || '',
        cookie_banner_enabled: !!response.data.cookie_banner_enabled,
        cookie_banner_title: response.data.cookie_banner_title || '',
        cookie_banner_message: response.data.cookie_banner_message || '',
        cookie_banner_accept_text: response.data.cookie_banner_accept_text || '',
        cookie_banner_reject_text: response.data.cookie_banner_reject_text || '',
        cookie_banner_policy_url: response.data.cookie_banner_policy_url || '',
        physical_address: response.data.physical_address || '',
      };
      setBranding(next);
      setAddressDraft(next.physical_address);
      setCustomCodeDraft({
        custom_head_html: next.custom_head_html,
        custom_body_html: next.custom_body_html,
      });
      setBannerDraft({
        cookie_banner_enabled: next.cookie_banner_enabled,
        cookie_banner_title: next.cookie_banner_title,
        cookie_banner_message: next.cookie_banner_message,
        cookie_banner_accept_text: next.cookie_banner_accept_text,
        cookie_banner_reject_text: next.cookie_banner_reject_text,
        cookie_banner_policy_url: next.cookie_banner_policy_url,
      });
    } catch (e) {
      console.error('Failed to load branding', e);
    }
  };

  const saveBranding = async (next) => {
    setSavingBranding(true);
    try {
      const res = await api.put('/tenant/branding', next);
      setBranding({
        logo_url: res.data.logo_url || '',
        favicon_url: res.data.favicon_url || '',
        custom_head_html: res.data.custom_head_html || '',
        custom_body_html: res.data.custom_body_html || '',
        cookie_banner_enabled: !!res.data.cookie_banner_enabled,
        cookie_banner_title: res.data.cookie_banner_title || '',
        cookie_banner_message: res.data.cookie_banner_message || '',
        cookie_banner_accept_text: res.data.cookie_banner_accept_text || '',
        cookie_banner_reject_text: res.data.cookie_banner_reject_text || '',
        cookie_banner_policy_url: res.data.cookie_banner_policy_url || '',
        physical_address: res.data.physical_address || '',
      });
      toast.success('Branding saved');
      // Update favicon live without reload — covers ALL pages in the SPA
      // (sidebar, dashboard, public previews) via the global BrandingContext.
      if (next.favicon_url !== undefined) {
        refreshBranding();
      }
    } catch (e) {
      toast.error('Failed to save branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const saveCustomCode = async () => {
    await saveBranding({
      custom_head_html: customCodeDraft.custom_head_html,
      custom_body_html: customCodeDraft.custom_body_html,
    });
  };

  const saveBanner = async () => {
    await saveBranding({
      cookie_banner_enabled: bannerDraft.cookie_banner_enabled,
      cookie_banner_title: bannerDraft.cookie_banner_title,
      cookie_banner_message: bannerDraft.cookie_banner_message,
      cookie_banner_accept_text: bannerDraft.cookie_banner_accept_text,
      cookie_banner_reject_text: bannerDraft.cookie_banner_reject_text,
      cookie_banner_policy_url: bannerDraft.cookie_banner_policy_url,
    });
  };

  // True when the banner draft differs from the saved state (drives the dirty
  // indicator + Save button enablement).
  const bannerDirty =
    bannerDraft.cookie_banner_enabled !== branding.cookie_banner_enabled ||
    bannerDraft.cookie_banner_title !== branding.cookie_banner_title ||
    bannerDraft.cookie_banner_message !== branding.cookie_banner_message ||
    bannerDraft.cookie_banner_accept_text !== branding.cookie_banner_accept_text ||
    bannerDraft.cookie_banner_reject_text !== branding.cookie_banner_reject_text ||
    bannerDraft.cookie_banner_policy_url !== branding.cookie_banner_policy_url;

  const fetchTheme = async () => {
    try {
      const response = await api.get('/theme');
      setTheme(response.data);
    } catch (error) {
      toast.error('Failed to load theme settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPresets = async () => {
    try {
      const response = await api.get('/theme/presets');
      setPresets(response.data);
    } catch (error) {
      console.error('Failed to load presets');
    }
  };

  // Helper function to detect which preset matches the current theme
  const getActivePreset = () => {
    if (!theme || !presets || Object.keys(presets).length === 0) return null;
    
    for (const [key, preset] of Object.entries(presets)) {
      // Check if primary colors match (main indicator of preset)
      const colorsMatch = 
        theme.primary_color?.toLowerCase() === preset.primary_color?.toLowerCase() &&
        theme.background_color?.toLowerCase() === preset.background_color?.toLowerCase();
      
      if (colorsMatch) {
        return { key, name: preset.name };
      }
    }
    return null; // Custom theme (no preset match)
  };

  const activePreset = getActivePreset();

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/theme', theme);
      toast.success('Theme settings saved!');
    } catch (error) {
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = async (presetName) => {
    // "Default" is destructive — it replaces ALL current theme values with the
    // app's built-in factory defaults. Confirm before applying so users don't
    // wipe their branding by accident.
    if (presetName === 'default') {
      const ok = window.confirm(
        'Reset your Site Theme to the built-in Default look?\n\n' +
        'This will replace every colour, font and shape setting with the app defaults ' +
        '(indigo primary, Manrope headings, Inter body, white background, rounded corners).\n\n' +
        'Your logo and favicon are NOT affected. Anything you have set — colours, fonts, ' +
        'font sizes, shadows, spacing — will be overwritten.'
      );
      if (!ok) return;
    }
    try {
      const response = await api.post(`/theme/apply-preset/${presetName}`);
      setTheme(response.data);
      toast.success(`Applied "${presets[presetName]?.name}" theme`);
    } catch (error) {
      toast.error('Failed to apply preset');
    }
  };

  const updateTheme = (key, value) => {
    setTheme(prev => ({ ...prev, [key]: value }));
  };

  // Use shared font families from constants (same as BlockEditor toolbar)
  // Extract just the primary font name for matching with theme values
  const fontOptions = FONT_FAMILIES
    .filter(f => f.value !== 'inherit')
    .map(f => ({
      value: f.value.split(',')[0].trim(), // Extract primary font name (e.g., "Manrope" from "Manrope, sans-serif")
      label: f.label,
      fullValue: f.value
    }));

  const borderRadiusOptions = [
    { value: 'sharp', label: 'Sharp (0px)' },
    { value: 'subtle', label: 'Subtle (4px)' },
    { value: 'rounded', label: 'Rounded (8px)' },
    { value: 'more-rounded', label: 'More Rounded (16px)' },
    { value: 'pill', label: 'Pill (Full)' },
  ];

  const shadowOptions = [
    { value: 'none', label: 'None' },
    { value: 'subtle', label: 'Subtle' },
    { value: 'medium', label: 'Medium' },
    { value: 'dramatic', label: 'Dramatic' },
  ];

  const spacingOptions = [
    { value: 'compact', label: 'Compact' },
    { value: 'normal', label: 'Normal' },
    { value: 'spacious', label: 'Spacious' },
  ];

  const animationOptions = [
    { value: 'none', label: 'None' },
    { value: 'fadeIn', label: 'Fade In' },
    { value: 'fadeInUp', label: 'Fade In Up' },
    { value: 'fadeInDown', label: 'Fade In Down' },
    { value: 'slideLeft', label: 'Slide from Left' },
    { value: 'slideRight', label: 'Slide from Right' },
    { value: 'zoomIn', label: 'Zoom In' },
    { value: 'bounce', label: 'Bounce' },
  ];

  const hoverEffectOptions = [
    { value: 'none', label: 'None' },
    { value: 'lift', label: 'Lift' },
    { value: 'glow', label: 'Glow' },
    { value: 'darken', label: 'Darken' },
    { value: 'scale', label: 'Scale' },
    { value: 'shadow', label: 'Shadow' },
  ];

  const clickEffectOptions = [
    { value: 'none', label: 'None' },
    { value: 'scale', label: 'Scale Down' },
    { value: 'flip', label: 'Flip' },
    { value: 'shake', label: 'Shake' },
    { value: 'highlight', label: 'Highlight' },
    { value: 'color-change', label: 'Color Change' },
  ];

  const buttonStyleOptions = [
    { value: 'solid', label: 'Solid' },
    { value: 'outline', label: 'Outline' },
    { value: 'ghost', label: 'Ghost' },
    { value: 'soft', label: 'Soft' },
  ];

  const textOverlayOptions = [
    { value: 'none', label: 'None' },
    { value: 'fade-in', label: 'Fade In' },
    { value: 'slide-up', label: 'Slide Up' },
    { value: 'slide-down', label: 'Slide Down' },
    { value: 'typewriter', label: 'Typewriter' },
    { value: 'zoom', label: 'Zoom' },
  ];

  const gradientDirections = [
    { value: 'to bottom', label: 'Top to Bottom' },
    { value: 'to top', label: 'Bottom to Top' },
    { value: 'to right', label: 'Left to Right' },
    { value: 'to left', label: 'Right to Left' },
    { value: 'to bottom right', label: 'Diagonal ↘' },
    { value: 'to bottom left', label: 'Diagonal ↙' },
    { value: 'to top right', label: 'Diagonal ↗' },
    { value: 'to top left', label: 'Diagonal ↖' },
  ];

  if (loading || !theme) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-slate-50 p-8 flex items-center justify-center">
          <p className="text-slate-500">Loading theme settings...</p>
        </div>
      </div>
    );
  }

  // Generate preview background style
  const getPreviewBackground = () => {
    if (theme.background_type === 'linear-gradient') {
      return `linear-gradient(${theme.gradient_direction || 'to bottom'}, ${theme.gradient_start || '#6366f1'}, ${theme.gradient_end || '#8b5cf6'})`;
    } else if (theme.background_type === 'radial-gradient') {
      return `radial-gradient(circle, ${theme.gradient_start || '#6366f1'}, ${theme.gradient_end || '#8b5cf6'})`;
    }
    return theme.background_color || '#ffffff';
  };

  return (
    <div className="flex" data-testid="theme-settings-page">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50">
        {/* Header */}
        <div className="border-b border-slate-200 bg-white px-8 py-4 flex justify-between items-center sticky top-0 z-10">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Site Theme Settings
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-slate-500 text-sm">Customize the look and feel of your site</p>
              {theme && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                  <span 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: theme.primary_color }}
                  />
                  {activePreset ? activePreset.name : 'Custom Theme'}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={fetchTheme}
              title="Discard any unsaved changes and reload the last saved theme settings"
            >
              <RotateCcw size={16} className="mr-2" />
              Reset
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="p-8 max-w-6xl mx-auto">
          {/* Theme Presets */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Palette size={20} />
              Quick Presets
            </h2>
            
            {/* General Themes */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Basic</h3>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-3">
                {Object.entries(presets)
                  .filter(([key]) => ['default', 'light', 'dark', 'warm', 'cool', 'gradient-purple', 'gradient-ocean', 'gradient-sunset'].includes(key))
                  .sort(([a], [b]) => {
                    // Force Default to appear first
                    if (a === 'default') return -1;
                    if (b === 'default') return 1;
                    return 0;
                  })
                  .map(([key, preset]) => {
                    const isActive = activePreset?.key === key;
                    const isDefault = key === 'default';
                    const tile = (
                      <button
                        key={key}
                        onClick={() => applyPreset(key)}
                        data-testid={`theme-preset-${key}`}
                        className={`group p-3 rounded-lg border-2 transition-all text-center relative w-full ${
                          isActive 
                            ? 'border-indigo-500 ring-2 ring-indigo-200' 
                            : isDefault
                              ? 'border-dashed border-slate-300 hover:border-indigo-400 bg-slate-50/50'
                              : 'border-slate-200 hover:border-indigo-400'
                        }`}
                      >
                        {isActive && (
                          <div className="absolute -top-2 -right-2 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                        <div 
                          className="w-full h-12 rounded-md mb-2 shadow-inner flex items-center justify-center"
                          style={isDefault ? { background: '#f8fafc', color: '#64748b' } : { 
                            background: preset.background_type?.includes('gradient') 
                              ? `linear-gradient(to bottom right, ${preset.gradient_start}, ${preset.gradient_end})`
                              : preset.background_color 
                          }}
                        >
                          {isDefault && <RotateCcw size={18} className="text-slate-400" />}
                        </div>
                        <span className={`text-xs font-medium ${isActive ? 'text-indigo-600' : 'text-slate-600 group-hover:text-indigo-600'}`}>
                          {preset.name}
                        </span>
                      </button>
                    );
                    return isDefault ? (
                      <SimpleTooltip
                        key={key}
                        content={
                          <span className="block max-w-xs text-left">
                            <span className="block font-semibold mb-1">Default</span>
                            <span className="block text-xs leading-relaxed">
                              Resets your entire Site Theme back to the built-in factory look —
                              indigo primary colour, Manrope headings, Inter body font, white background,
                              subtle shadows and rounded corners. Use this if you want to start fresh.
                              Your logo and favicon are kept. Everything else is overwritten.
                            </span>
                          </span>
                        }
                        side="top"
                      >
                        <div>{tile}</div>
                      </SimpleTooltip>
                    ) : tile;
                  })}
              </div>
            </div>

            {/* Professional Themes */}
            <div>
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <span className="text-amber-500">★</span> Professional Themes
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(presets)
                  .filter(([key]) => !['default', 'light', 'dark', 'warm', 'cool', 'gradient-purple', 'gradient-ocean', 'gradient-sunset'].includes(key))
                  .map(([key, preset]) => {
                    const isActive = activePreset?.key === key;
                    return (
                      <button
                        key={key}
                        onClick={() => applyPreset(key)}
                        className={`group p-4 rounded-xl border-2 transition-all text-left relative ${
                          isActive 
                            ? 'border-indigo-500 ring-2 ring-indigo-200 shadow-lg' 
                            : 'border-slate-200 hover:border-indigo-400 hover:shadow-lg'
                        }`}
                      >
                        {isActive && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center shadow-md">
                            <Check size={14} className="text-white" />
                          </div>
                        )}
                        <div 
                          className="w-full h-16 rounded-lg mb-3 shadow-inner flex items-end p-2"
                          style={{ 
                            background: preset.background_type?.includes('gradient') 
                              ? `linear-gradient(to bottom right, ${preset.gradient_start || preset.primary_color}, ${preset.gradient_end || preset.secondary_color})`
                              : preset.primary_color || preset.background_color 
                          }}
                        >
                          <div className="flex gap-1">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: preset.primary_color }} />
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: preset.secondary_color || preset.accent_color }} />
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: preset.accent_color }} />
                          </div>
                        </div>
                        <span className={`text-sm font-semibold block ${isActive ? 'text-indigo-600' : 'text-slate-700 group-hover:text-indigo-600'}`}>
                          {preset.name?.replace(' (School)', '')}
                        </span>
                        {preset.description && (
                          <span className="text-xs text-slate-500 mt-1 block line-clamp-2">
                            {preset.description}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Live Preview */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Live Preview</h2>
            <div 
              className="rounded-lg p-8 min-h-[200px] transition-all"
              style={{ 
                background: getPreviewBackground(),
                fontFamily: theme.body_font
              }}
            >
              <h1 
                className="font-bold mb-2"
                style={{ 
                  color: theme.heading_color, 
                  fontFamily: theme.heading_font,
                  fontSize: `${theme.h1_size || 48}px`
                }}
              >
                H1 Heading
              </h1>
              <h2 
                className="font-bold mb-2"
                style={{ 
                  color: theme.heading_color, 
                  fontFamily: theme.heading_font,
                  fontSize: `${theme.h2_size || 36}px`
                }}
              >
                H2 Heading
              </h2>
              <h3 
                className="font-semibold mb-4"
                style={{ 
                  color: theme.heading_color, 
                  fontFamily: theme.heading_font,
                  fontSize: `${theme.h3_size || 28}px`
                }}
              >
                H3 Heading
              </h3>
              <p style={{ color: theme.body_color, fontSize: `${theme.body_size || 16}px` }} className="mb-4">
                This is how your body text will look. The quick brown fox jumps over the lazy dog.
              </p>
              <p style={{ color: theme.muted_color, fontSize: `${theme.small_size || 14}px` }} className="mb-4">
                This is smaller/muted text for captions and secondary content.
              </p>
              <a href="#" style={{ color: theme.link_color, fontSize: `${theme.body_size || 16}px` }} className="underline mb-4 inline-block">
                This is a link
              </a>
              <div className="mt-4 flex gap-3">
                <button 
                  className="px-4 py-2 rounded transition-all"
                  style={{ 
                    backgroundColor: theme.button_style === 'solid' ? theme.primary_color : 'transparent',
                    color: theme.button_style === 'solid' ? '#fff' : theme.primary_color,
                    border: theme.button_style !== 'solid' ? `2px solid ${theme.primary_color}` : 'none',
                    borderRadius: theme.border_radius === 'pill' ? '9999px' : theme.border_radius === 'rounded' ? '8px' : '4px'
                  }}
                >
                  Primary Button
                </button>
                <button 
                  className="px-4 py-2 rounded transition-all"
                  style={{ 
                    backgroundColor: 'transparent',
                    color: theme.secondary_color,
                    border: `2px solid ${theme.secondary_color}`,
                    borderRadius: theme.border_radius === 'pill' ? '9999px' : theme.border_radius === 'rounded' ? '8px' : '4px'
                  }}
                >
                  Secondary Button
                </button>
              </div>
            </div>
          </div>

          {/* Settings Tabs */}
          <Tabs defaultValue="background" className="bg-white rounded-xl border border-slate-200">
            <TabsList className="w-full border-b border-slate-200 rounded-none bg-slate-50 p-1">
              <TabsTrigger value="background" className="flex-1">
                <Palette size={16} className="mr-2" /> Background
              </TabsTrigger>
              <TabsTrigger value="typography" className="flex-1">
                <Type size={16} className="mr-2" /> Typography
              </TabsTrigger>
              <TabsTrigger value="colors" className="flex-1">
                <Palette size={16} className="mr-2" /> Colors
              </TabsTrigger>
              <TabsTrigger value="shape" className="flex-1">
                <Square size={16} className="mr-2" /> Shape & Style
              </TabsTrigger>
              <TabsTrigger value="branding" className="flex-1" data-testid="branding-tab">
                <ImageIcon size={16} className="mr-2" /> Branding
              </TabsTrigger>
              <TabsTrigger value="custom-code" className="flex-1" data-testid="custom-code-tab">
                <Code size={16} className="mr-2" /> Custom Code
              </TabsTrigger>
            </TabsList>

            {/* Background Tab */}
            <TabsContent value="background" className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Background Type</Label>
                  <Select value={theme.background_type} onValueChange={(v) => updateTheme('background_type', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">Solid Color</SelectItem>
                      <SelectItem value="linear-gradient">Linear Gradient</SelectItem>
                      <SelectItem value="radial-gradient">Radial Gradient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {theme.background_type === 'solid' && (
                  <div>
                    <Label>Background Color</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="color"
                        value={theme.background_color}
                        onChange={(e) => updateTheme('background_color', e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={theme.background_color}
                        onChange={(e) => updateTheme('background_color', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                )}

                {theme.background_type !== 'solid' && (
                  <>
                    <div>
                      <Label>Gradient Direction</Label>
                      <Select value={theme.gradient_direction} onValueChange={(v) => updateTheme('gradient_direction', v)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {gradientDirections.map(d => (
                            <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Gradient Start Color</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="color"
                          value={theme.gradient_start || '#6366f1'}
                          onChange={(e) => updateTheme('gradient_start', e.target.value)}
                          className="w-16 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={theme.gradient_start || '#6366f1'}
                          onChange={(e) => updateTheme('gradient_start', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Gradient End Color</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="color"
                          value={theme.gradient_end || '#8b5cf6'}
                          onChange={(e) => updateTheme('gradient_end', e.target.value)}
                          className="w-16 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={theme.gradient_end || '#8b5cf6'}
                          onChange={(e) => updateTheme('gradient_end', e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* Typography Tab */}
            <TabsContent value="typography" className="p-6">
              <div className="space-y-6">
                {/* Font Family Section */}
                <div>
                  <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Font Families</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <Label>Heading Font</Label>
                      <Select value={theme.heading_font} onValueChange={(v) => updateTheme('heading_font', v)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fontOptions.map(f => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Body Font</Label>
                      <Select value={theme.body_font} onValueChange={(v) => updateTheme('body_font', v)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fontOptions.map(f => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Font Sizes Section */}
                <div>
                  <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Font Sizes</h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <Label>H1 Size (px)</Label>
                      <Input
                        type="number"
                        min="16"
                        max="96"
                        value={theme.h1_size || 48}
                        onChange={(e) => updateTheme('h1_size', parseInt(e.target.value) || 48)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>H2 Size (px)</Label>
                      <Input
                        type="number"
                        min="14"
                        max="72"
                        value={theme.h2_size || 36}
                        onChange={(e) => updateTheme('h2_size', parseInt(e.target.value) || 36)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>H3 Size (px)</Label>
                      <Input
                        type="number"
                        min="12"
                        max="48"
                        value={theme.h3_size || 28}
                        onChange={(e) => updateTheme('h3_size', parseInt(e.target.value) || 28)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>H4 Size (px)</Label>
                      <Input
                        type="number"
                        min="12"
                        max="36"
                        value={theme.h4_size || 22}
                        onChange={(e) => updateTheme('h4_size', parseInt(e.target.value) || 22)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Body Size (px)</Label>
                      <Input
                        type="number"
                        min="12"
                        max="24"
                        value={theme.body_size || 16}
                        onChange={(e) => updateTheme('body_size', parseInt(e.target.value) || 16)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Small Text Size (px)</Label>
                      <Input
                        type="number"
                        min="10"
                        max="18"
                        value={theme.small_size || 14}
                        onChange={(e) => updateTheme('small_size', parseInt(e.target.value) || 14)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    These sizes will be used as defaults across your site. Individual blocks can override these settings.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Colors Tab */}
            <TabsContent value="colors" className="p-6">
              <p className="text-xs text-slate-500 mb-4">Hover the <Info size={12} className="inline -mt-0.5 mx-0.5" /> icon next to each colour to see where it's applied across your site.</p>
              <div className="grid grid-cols-3 gap-6">
                {[
                  { key: 'heading_color', label: 'Heading Color', tip: 'Applied to all H1–H6 headings across pages, blog articles, hero subheadings and section titles.' },
                  { key: 'body_color', label: 'Body Text Color', tip: 'The main paragraph colour. Used for body copy in pages, articles, descriptions, and form labels.' },
                  { key: 'muted_color', label: 'Muted Text Color', tip: 'Secondary text — captions, dates, metadata, footer copyright, image credits, and field helper text.' },
                  { key: 'primary_color', label: 'Primary Color', tip: 'Your main brand colour. Used on primary buttons, active nav items, link underlines, focus rings, and key call-to-actions.' },
                  { key: 'secondary_color', label: 'Secondary Color', tip: 'Used on secondary/outline buttons, hover states, and supporting accents around the primary colour.' },
                  { key: 'accent_color', label: 'Accent Color', tip: 'Used for badges, tags, pricing-card highlights, alerts, and small decorative touches that need attention.' },
                  { key: 'link_color', label: 'Link Color', tip: 'Default colour of clickable text links in body content and articles.' },
                  { key: 'link_hover_color', label: 'Link Hover Color', tip: 'Colour links shift to when the visitor hovers over them. Usually a darker shade of the link colour.' },
                  { key: 'border_color', label: 'Border Color', tip: 'Subtle borders used on cards, dividers, form inputs, tables, and section separators.' },
                ].map(({ key, label, tip }) => (
                  <div key={key}>
                    <div className="flex items-center gap-1">
                      <Label>{label}</Label>
                      <SimpleTooltip content={tip} side="top">
                        <span className="cursor-help text-slate-400 hover:text-slate-600">
                          <Info size={13} />
                        </span>
                      </SimpleTooltip>
                    </div>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="color"
                        value={theme[key] || '#000000'}
                        onChange={(e) => updateTheme(key, e.target.value)}
                        className="w-16 h-10 p-1 cursor-pointer"
                        data-testid={`theme-color-${key}`}
                      />
                      <Input
                        value={theme[key] || '#000000'}
                        onChange={(e) => updateTheme(key, e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Shape & Style Tab */}
            <TabsContent value="shape" className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Border Radius</Label>
                  <Select value={theme.border_radius} onValueChange={(v) => updateTheme('border_radius', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {borderRadiusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Shadow Preset</Label>
                  <Select value={theme.shadow_preset} onValueChange={(v) => updateTheme('shadow_preset', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {shadowOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Spacing Scale</Label>
                  <Select value={theme.spacing_scale} onValueChange={(v) => updateTheme('spacing_scale', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {spacingOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Button Style</Label>
                  <Select value={theme.button_style} onValueChange={(v) => updateTheme('button_style', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {buttonStyleOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Animations Tab */}
            <TabsContent value="animations" className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Default Page Load Animation</Label>
                  <Select value={theme.default_animation} onValueChange={(v) => updateTheme('default_animation', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {animationOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Animation Duration</Label>
                  <Select value={theme.animation_duration} onValueChange={(v) => updateTheme('animation_duration', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.3s">Fast (0.3s)</SelectItem>
                      <SelectItem value="0.5s">Normal (0.5s)</SelectItem>
                      <SelectItem value="0.8s">Slow (0.8s)</SelectItem>
                      <SelectItem value="1.2s">Very Slow (1.2s)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Text Over Image Animation</Label>
                  <Select value={theme.text_overlay_animation} onValueChange={(v) => updateTheme('text_overlay_animation', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {textOverlayOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Interactions Tab */}
            <TabsContent value="interactions" className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Default Hover Effect</Label>
                  <Select value={theme.hover_effect} onValueChange={(v) => updateTheme('hover_effect', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hoverEffectOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Button Hover Effect</Label>
                  <Select value={theme.button_hover_effect} onValueChange={(v) => updateTheme('button_hover_effect', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hoverEffectOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Click Effect</Label>
                  <Select value={theme.click_effect} onValueChange={(v) => updateTheme('click_effect', v)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {clickEffectOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Branding Tab */}
            <TabsContent value="branding" className="p-6" data-testid="branding-tab-content">
              <div className="max-w-2xl space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Site Branding</h3>
                  <p className="text-sm text-slate-500">
                    Upload your logo and favicon. The favicon appears in browser tabs and bookmarks.
                  </p>
                </div>

                {/* Logo */}
                <div className="rounded-lg border border-slate-200 p-5 bg-white">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <Label className="text-sm font-semibold">Site Logo</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Used in navigation, footer, and login screens. Recommended: transparent PNG, height ~80px.</p>
                    </div>
                    {branding.logo_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => saveBranding({ logo_url: '' })}
                        className="text-red-500 hover:text-red-600"
                        data-testid="remove-logo-btn"
                      >
                        <X size={14} className="mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  {branding.logo_url ? (
                    <div className="flex items-center gap-4">
                      <div className="w-48 h-24 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center p-3">
                        <img src={branding.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setBrandingPickerTarget('logo')}
                        data-testid="change-logo-btn"
                      >
                        Change Logo
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setBrandingPickerTarget('logo')}
                      className="w-full max-w-md h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
                      data-testid="pick-logo-btn"
                    >
                      <ImageIcon size={24} className="text-slate-400" />
                      <span className="text-xs text-slate-500">Choose Logo from Media Library</span>
                    </button>
                  )}
                </div>

                {/* Favicon */}
                <div className="rounded-lg border border-slate-200 p-5 bg-white">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <Label className="text-sm font-semibold">Favicon</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Tiny icon shown in browser tabs and bookmarks. Recommended: square PNG/SVG, 32×32 to 256×256.</p>
                    </div>
                    {branding.favicon_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => saveBranding({ favicon_url: '' })}
                        className="text-red-500 hover:text-red-600"
                        data-testid="remove-favicon-btn"
                      >
                        <X size={14} className="mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  {branding.favicon_url ? (
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center p-2">
                        <img src={branding.favicon_url} alt="Favicon" className="max-w-full max-h-full object-contain" />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setBrandingPickerTarget('favicon')}
                        data-testid="change-favicon-btn"
                      >
                        Change Favicon
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setBrandingPickerTarget('favicon')}
                      className="w-full max-w-md h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
                      data-testid="pick-favicon-btn"
                    >
                      <ImageIcon size={24} className="text-slate-400" />
                      <span className="text-xs text-slate-500">Choose Favicon from Media Library</span>
                    </button>
                  )}
                </div>

                {savingBranding && (
                  <p className="text-xs text-slate-500">Saving branding...</p>
                )}

                {/* Newsletter mailing address — legally required in the footer
                    of every bulk email (CAN-SPAM / CASL / AU Spam Act). */}
                <div className="rounded-lg border border-slate-200 p-5 bg-white space-y-2 mt-6" data-testid="newsletter-address-section">
                  <Label htmlFor="newsletter-address" className="text-sm font-semibold">Newsletter mailing address</Label>
                  <p className="text-xs text-slate-500">
                    Shown in the footer of every newsletter. A valid physical postal address is legally required for bulk email (CAN-SPAM / CASL / Australian Spam Act).
                  </p>
                  <textarea
                    id="newsletter-address"
                    value={addressDraft}
                    onChange={(e) => setAddressDraft(e.target.value)}
                    placeholder={'e.g. Springfield Primary School, 1 School Rd, Sydney NSW 2000, Australia'}
                    rows={2}
                    className="w-full text-sm px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                    data-testid="newsletter-address-input"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={() => saveBranding({ ...branding, physical_address: addressDraft })}
                      disabled={savingBranding || addressDraft === branding.physical_address}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-700 transition-colors"
                      data-testid="save-newsletter-address-btn"
                    >
                      Save address
                    </button>
                  </div>
                </div>

                {/* Language selector position — where the floating language
                    switcher appears on public pages. Only affects tenants
                    with translation enabled; doesn't render at all otherwise. */}
                <div className="rounded-lg border border-slate-200 p-5 bg-white space-y-2">
                  <Label className="text-sm font-semibold">Language selector position</Label>
                  <p className="text-xs text-slate-500">
                    Choose where the language switcher appears on your public site. Only affects sites with translation turned on.
                  </p>
                  <Select
                    value={theme.language_selector_position || 'floating'}
                    onValueChange={(v) => updateTheme('language_selector_position', v)}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="language-selector-position-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="floating">
                        <span className="inline-flex flex-col items-start">
                          <span className="font-medium">Floating pill</span>
                          <span className="text-xs text-slate-500">Bottom-left on mobile, top-right on desktop (default)</span>
                        </span>
                      </SelectItem>
                      <SelectItem value="top_bar">
                        <span className="inline-flex flex-col items-start">
                          <span className="font-medium">Top bar</span>
                          <span className="text-xs text-slate-500">Slim strip at the very top of the page, above your header</span>
                        </span>
                      </SelectItem>
                      <SelectItem value="hidden">
                        <span className="inline-flex flex-col items-start">
                          <span className="font-medium">Hidden</span>
                          <span className="text-xs text-slate-500">Don't render — place your own inside a custom-HTML block</span>
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Custom Code Tab — paste-in HTML snippets that get injected
                into every public page. Designed for chat widgets, analytics,
                font preloads, etc. */}
            <TabsContent value="custom-code" className="p-6" data-testid="custom-code-tab-content">
              <div className="max-w-3xl space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Custom Code</h3>
                  <p className="text-sm text-slate-500">
                    Paste HTML snippets (chat widgets, analytics, font preloads) that should appear on every public page.
                    Changes apply immediately the next time a visitor loads a page — no republish needed.
                  </p>
                </div>

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2">
                  <Info size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="font-semibold">Security:</strong> code here runs on your public site. Only paste snippets from trusted providers (e.g. Google Analytics, or an official chat-widget embed code). A bad snippet can break the page or steal visitor data.
                  </div>
                </div>

                {/* Head snippet */}
                <div className="rounded-lg border border-slate-200 p-5 bg-white space-y-2">
                  <Label htmlFor="custom-head-html" className="text-sm font-semibold">Head Code <span className="text-slate-400 font-normal">(inside &lt;head&gt;)</span></Label>
                  <p className="text-xs text-slate-500">
                    Use this for things that must load early: Google Analytics / GTM, font preloads, meta verification tags.
                  </p>
                  <textarea
                    id="custom-head-html"
                    value={customCodeDraft.custom_head_html}
                    onChange={(e) => setCustomCodeDraft({ ...customCodeDraft, custom_head_html: e.target.value })}
                    placeholder={'<!-- Example: Google Analytics -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXX"></script>'}
                    rows={6}
                    className="w-full font-mono text-xs px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                    spellCheck={false}
                    data-testid="custom-head-html-input"
                  />
                </div>

                {/* Body snippet */}
                <div className="rounded-lg border border-slate-200 p-5 bg-white space-y-2">
                  <Label htmlFor="custom-body-html" className="text-sm font-semibold">Body Code <span className="text-slate-400 font-normal">(before &lt;/body&gt;)</span></Label>
                  <p className="text-xs text-slate-500">
                    Use this for widgets that don't need to block page load: <strong>chat widgets</strong>, Intercom, Crisp, Drift, etc. This is where most chat widget embed codes belong.
                  </p>
                  <textarea
                    id="custom-body-html"
                    value={customCodeDraft.custom_body_html}
                    onChange={(e) => setCustomCodeDraft({ ...customCodeDraft, custom_body_html: e.target.value })}
                    placeholder={'<!-- Example: chat widget -->\n<script src="https://example.com/widget.js" data-id="XXXX"></script>'}
                    rows={8}
                    className="w-full font-mono text-xs px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                    spellCheck={false}
                    data-testid="custom-body-html-input"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">
                    {customCodeDraft.custom_head_html === branding.custom_head_html
                     && customCodeDraft.custom_body_html === branding.custom_body_html
                      ? 'No unsaved changes'
                      : 'Unsaved changes'}
                  </p>
                  <Button
                    type="button"
                    onClick={saveCustomCode}
                    disabled={savingBranding ||
                      (customCodeDraft.custom_head_html === branding.custom_head_html
                       && customCodeDraft.custom_body_html === branding.custom_body_html)}
                    data-testid="save-custom-code-btn"
                  >
                    <Save size={16} className="mr-2" />
                    {savingBranding ? 'Saving…' : 'Save Custom Code'}
                  </Button>
                </div>

                {/* Cookie Consent Banner — GDPR / Privacy Act friendly. When
                    enabled, the Custom Code snippets above are NOT executed
                    until the visitor clicks Accept. */}
                <div className="rounded-lg border border-slate-200 p-5 bg-white space-y-4 mt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <Label className="text-sm font-semibold flex items-center gap-2">
                        Cookie Consent Banner
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">
                        Show a small banner asking visitors to accept cookies before any of your Custom Code scripts (chat widgets, analytics) run. Helps with GDPR, Australian Privacy Act, and similar compliance.
                      </p>
                    </div>
                    {/* Toggle switch */}
                    <button
                      type="button"
                      role="switch"
                      aria-checked={bannerDraft.cookie_banner_enabled}
                      onClick={() => setBannerDraft({ ...bannerDraft, cookie_banner_enabled: !bannerDraft.cookie_banner_enabled })}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${bannerDraft.cookie_banner_enabled ? 'bg-indigo-600' : 'bg-slate-300'}`}
                      data-testid="cookie-banner-toggle"
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${bannerDraft.cookie_banner_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>

                  {bannerDraft.cookie_banner_enabled && (
                    <div className="space-y-3 pl-1">
                      <div>
                        <Label htmlFor="banner-title" className="text-xs font-medium text-slate-700">Title</Label>
                        <input
                          id="banner-title"
                          type="text"
                          value={bannerDraft.cookie_banner_title}
                          onChange={(e) => setBannerDraft({ ...bannerDraft, cookie_banner_title: e.target.value })}
                          placeholder="We use cookies"
                          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          data-testid="banner-title-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="banner-message" className="text-xs font-medium text-slate-700">Message</Label>
                        <textarea
                          id="banner-message"
                          rows={3}
                          value={bannerDraft.cookie_banner_message}
                          onChange={(e) => setBannerDraft({ ...bannerDraft, cookie_banner_message: e.target.value })}
                          placeholder="We use cookies to enhance your browsing experience and analyse our traffic. By clicking Accept, you consent to our use of cookies."
                          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                          data-testid="banner-message-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="banner-accept" className="text-xs font-medium text-slate-700">Accept button text</Label>
                          <input
                            id="banner-accept"
                            type="text"
                            value={bannerDraft.cookie_banner_accept_text}
                            onChange={(e) => setBannerDraft({ ...bannerDraft, cookie_banner_accept_text: e.target.value })}
                            placeholder="Accept all"
                            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            data-testid="banner-accept-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="banner-reject" className="text-xs font-medium text-slate-700">Reject button text</Label>
                          <input
                            id="banner-reject"
                            type="text"
                            value={bannerDraft.cookie_banner_reject_text}
                            onChange={(e) => setBannerDraft({ ...bannerDraft, cookie_banner_reject_text: e.target.value })}
                            placeholder="Reject"
                            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            data-testid="banner-reject-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="banner-policy" className="text-xs font-medium text-slate-700">Privacy policy URL (optional)</Label>
                        <input
                          id="banner-policy"
                          type="text"
                          value={bannerDraft.cookie_banner_policy_url}
                          onChange={(e) => setBannerDraft({ ...bannerDraft, cookie_banner_policy_url: e.target.value })}
                          placeholder="/privacy-policy"
                          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          data-testid="banner-policy-input"
                        />
                        <p className="text-[11px] text-slate-400 mt-1">If set, shows a "Learn more" link inside the banner.</p>
                      </div>

                      {/* Live preview */}
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Live preview</p>
                        <div className="rounded-xl bg-slate-900 text-white p-4 max-w-md">
                          <strong className="text-sm font-semibold">{bannerDraft.cookie_banner_title || 'We use cookies'}</strong>
                          <p className="text-xs mt-1 opacity-90">{bannerDraft.cookie_banner_message || 'We use cookies to enhance your browsing experience and analyse our traffic.'}{bannerDraft.cookie_banner_policy_url ? <span> <a href={bannerDraft.cookie_banner_policy_url} className="underline" target="_blank" rel="noreferrer">Learn more</a></span> : null}</p>
                          <div className="flex gap-2 mt-3">
                            <span className="flex-1 text-center text-xs font-semibold bg-indigo-600 rounded-md py-2">{bannerDraft.cookie_banner_accept_text || 'Accept all'}</span>
                            <span className="flex-1 text-center text-xs font-medium border border-white/20 rounded-md py-2">{bannerDraft.cookie_banner_reject_text || 'Reject'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <p className="text-xs text-slate-500">{bannerDirty ? 'Unsaved changes' : 'No unsaved changes'}</p>
                    <Button
                      type="button"
                      onClick={saveBanner}
                      disabled={savingBranding || !bannerDirty}
                      data-testid="save-banner-btn"
                    >
                      <Save size={16} className="mr-2" />
                      {savingBranding ? 'Saving…' : 'Save Banner Settings'}
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Branding Media Picker */}
      <MediaPickerModal
        open={!!brandingPickerTarget}
        onClose={() => setBrandingPickerTarget(null)}
        onSelect={(media) => {
          const next = brandingPickerTarget === 'favicon'
            ? { favicon_url: media.url }
            : { logo_url: media.url };
          saveBranding(next);
          setBrandingPickerTarget(null);
        }}
      />
    </div>
  );
};

export default ThemeSettings;
